import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Highcharts from 'highcharts';
import { Box, Typography, Button } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import {
  formatDaysHours,
  getUniqueDealerships,
  getDealershipAverageForRange,
  getGroupAverageForRange,
  getDealershipVehicleCount,
} from './chartUtils';

const GroupAveragesChart = ({ range, reportDepartmentData, dealershipReportHours, department_id, departmentName: departmentNameProp }) => {
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [visibleDealers, setVisibleDealers] = useState(null);
  const departments = useSelector(state => state.settings_departments.departments || []);

  // Define a color palette for unique dealership colors
  const colorPalette = [
    '#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8',
    '#6610f2', '#fd7e14', '#6f42c1', '#e83e8c', '#20c997',
  ];

  const departmentName = useMemo(() => {
    if (departmentNameProp) return departmentNameProp;
    const department = departments.find(d => parseInt(d.id) === parseInt(department_id));
    return department ? (department.display_name || department.name || 'Unknown') : 'Unknown';
  }, [departments, department_id, departmentNameProp]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!range || !reportDepartmentData || !Array.isArray(reportDepartmentData)) {
      return { categories: [], dataPoints: [], vehicleCounts: [], groupAverage: null };
    }
    const uniqueDealerships = getUniqueDealerships(reportDepartmentData);
    const dealershipAverages = uniqueDealerships.map((dealership, index) => {
      const average = getDealershipAverageForRange(dealership, range, reportDepartmentData, dealershipReportHours, department_id);
      const vehicleCount = getDealershipVehicleCount(dealership, range, reportDepartmentData, department_id);
      return {
        name: dealership,
        average,
        color: colorPalette[index % colorPalette.length],
        vehicleCount,
      };
    });
    const validAverages = dealershipAverages.filter(item => item.average !== null && item.average > 0 && item.name);
    const categories = validAverages.map(item => item.name);
    const dataPoints = validAverages.map((item, index) => ({
      y: Number(item.average) || 0,
      color: item.color,
    }));
    const vehicleCounts = validAverages.map(item => item.vehicleCount || 0);
    const groupAverage = getGroupAverageForRange(range, reportDepartmentData, dealershipReportHours, null, department_id);
    return { categories, dataPoints, vehicleCounts, groupAverage };
  }, [range, reportDepartmentData, dealershipReportHours, department_id]);

  // Initialize visibleDealers
  useEffect(() => {
    if (!visibleDealers && chartData.categories.length > 0) {
      const initialVisible = chartData.categories.reduce((acc, dealer) => ({ ...acc, [dealer]: true }), {});
      setVisibleDealers(initialVisible);
    } else if (!visibleDealers && chartData.categories.length === 0) {
      setVisibleDealers({});
    }
  }, [chartData.categories, visibleDealers]);

  // Filter chart data based on visible dealers
  const filteredChartData = useMemo(() => {
    if (!chartData.categories.length || !visibleDealers) {
      return chartData;
    }
    const filteredCategories = chartData.categories.filter((_, index) => visibleDealers[chartData.categories[index]]);
    const filteredDataPoints = chartData.dataPoints.filter((_, index) => visibleDealers[chartData.categories[index]]);
    const filteredVehicleCounts = chartData.vehicleCounts.filter((_, index) => visibleDealers[chartData.categories[index]]);
    const filteredAverage = getGroupAverageForRange(range, reportDepartmentData, dealershipReportHours, visibleDealers, department_id);
    return {
      categories: filteredCategories,
      dataPoints: filteredDataPoints,
      vehicleCounts: filteredVehicleCounts,
      groupAverage: filteredAverage,
    };
  }, [chartData, visibleDealers, range, reportDepartmentData, dealershipReportHours, department_id]);

  const toggleDealer = (dealer) => {
    setVisibleDealers(prev => ({
      ...prev,
      [dealer]: !prev[dealer],
    }));
  };

  // Export to PDF
  const handleExportToPDF = () => {
    const doc = new jsPDF();
    const now = new Date();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const year = now.getFullYear();
    const filename = `group_averages_${range}_${month}_${day}_${year}.pdf`;

    // Add title
    doc.setFontSize(16);
    doc.text(`${range}-day Group Averages (${departmentName})`, 10, 10);

    // Capture chart
    const element = containerRef.current;
    if (element) {
      html2canvas(element).then(canvas => {
        const imgData = canvas.toDataURL('image/png');
        const imgProps = doc.getImageProperties(imgData);
        const pdfWidth = doc.internal.pageSize.getWidth() - 20;
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        doc.addImage(imgData, 'PNG', 10, 20, pdfWidth, pdfHeight);

        // Add summary table
        if (filteredChartData.categories.length > 0) {
          const tableColumn = ['Dealership', 'Department', 'Average Time', 'Vehicle Count'];
          const tableRows = filteredChartData.categories.map((dealer, index) => [
            dealer,
            departmentName,
            formatDaysHours(filteredChartData.dataPoints[index]?.y) || 'N/A',
            filteredChartData.vehicleCounts[index] || 0,
          ]);
          autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20 + pdfHeight + 10,
            styles: { fontSize: 10 },
          });
        }

        // Add group average
        if (filteredChartData.groupAverage) {
          const finalY = doc.lastAutoTable.finalY || 20 + pdfHeight + 10;
          doc.setFontSize(12);
          doc.text(`Group Average: ${formatDaysHours(filteredChartData.groupAverage)}`, 10, finalY + 10);
        }

        doc.save(filename);
      }).catch(() => {});
    }
  };

  // Validate range
  const isValidRange = range && (['7', '14', '30', '90'].includes(range) || /^[0-9]{4}_[0-9]{2}$/.test(range));
  if (!isValidRange) {
    return <Typography>Invalid time range</Typography>;
  }

  useEffect(() => {
    if (containerRef.current) {
      try {
        if (filteredChartData.categories.length === 0) {
          chartRef.current = Highcharts.chart(containerRef.current, {
            chart: { type: 'column', backgroundColor: null },
            credits: { enabled: false },
            title: { text: `${range}-day Averages by Dealership`, style: { color: '#000' } },
            xAxis: { categories: ['No Data'], labels: { style: { color: '#000' } } },
            yAxis: {
              min: 0,
              title: { text: 'Average Time', style: { color: '#000' } },
              labels: {
                formatter: function () { return formatDaysHours(this.value); },
                style: { color: '#000' },
              },
            },
            series: [{ name: 'Average Time', data: [0] }],
            legend: { enabled: false },
          });
          return;
        }

        if (chartRef.current) {
          chartRef.current.series[0].setData(filteredChartData.dataPoints);
          chartRef.current.xAxis[0].setCategories(filteredChartData.categories);
          chartRef.current.yAxis[0].removePlotLine('average-line');
          if (filteredChartData.groupAverage) {
            chartRef.current.yAxis[0].addPlotLine({
              id: 'average-line',
              value: Number(filteredChartData.groupAverage),
              color: '#ff0000',
              width: 2,
              label: {
                text: `Group Average: ${formatDaysHours(filteredChartData.groupAverage)}`,
                align: 'center',
                style: { color: '#ff0000' },
              },
            });
          }
        } else {
          chartRef.current = Highcharts.chart(containerRef.current, {
            chart: {
              type: 'column',
              backgroundColor: null,
            },
            credits: {
              enabled: false,
            },
            title: {
              text: `${range}-day Averages by Dealership`,
              style: { color: '#000' },
            },
            xAxis: {
              categories: filteredChartData.categories,
              crosshair: true,
              labels: {
                style: { color: '#000' },
              },
            },
            yAxis: {
              min: 0,
              title: {
                text: 'Average Time',
                style: { color: '#000' },
              },
              plotLines: filteredChartData.groupAverage ? [{
                id: 'average-line',
                value: Number(filteredChartData.groupAverage),
                color: '#ff0000',
                width: 2,
                label: {
                  text: `Group Average: ${formatDaysHours(filteredChartData.groupAverage)}`,
                  align: 'center',
                  style: { color: '#ff0000' },
                },
              }] : [],
              labels: {
                formatter: function () {
                  return formatDaysHours(this.value);
                },
                style: { color: '#000' },
              },
            },
            tooltip: {
              formatter: function () {
                const color = this.series?.color || '#000';
                const name = this.series?.name || 'Average Time';
                const x = this.x || '';
                const y = this.y;
                return `<span style="font-size:10px">${x}</span><table>` +
                  `<tr><td style="color:${color};padding:0">${name}: </td>` +
                  `<td style="padding:0"><b>${formatDaysHours(y)}</b></td></tr></table>`;
              },
              shared: true,
              useHTML: true,
            },
            plotOptions: {
              column: {
                pointPadding: 0.2,
                borderWidth: 0,
                dataLabels: {
                  enabled: true,
                  rotation: -90,
                  color: '#000',
                  align: 'right',
                  formatter: function () {
                    return formatDaysHours(this.y);
                  },
                  y: 10,
                  style: {
                    fontSize: '13px',
                    color: '#000',
                    fontFamily: 'Verdana, sans-serif',
                  },
                },
              },
            },
            series: [{
              name: 'Average Time',
              data: filteredChartData.dataPoints,
            }],
            legend: {
              enabled: false,
            },
          });
        }
      } catch (error) {
        chartRef.current = null;
      }
    }

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [filteredChartData]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '600px', width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '8px' }}>
        <Button
          onClick={handleExportToPDF}
          startIcon={<FileDownloadIcon />}
          variant="contained"
          color="primary"
          size="small"
        >
          Export to PDF
        </Button>
      </Box>
      <Box sx={{ flex: '1', minHeight: 0 }}>
        <div id="group-chart" ref={containerRef} style={{ height: '100%', width: '100%' }} />
      </Box>
      <Box sx={{ flex: '1', minHeight: 0, padding: '8px' }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '8px' }}>Dealership Key</Typography>
        {chartData.categories.length > 0 ? (
          chartData.categories.map((dealer, index) => (
            <Box
              key={dealer}
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '4px',
                opacity: visibleDealers?.[dealer] ? 1 : 0.5,
                cursor: 'pointer',
              }}
              onClick={() => toggleDealer(dealer)}
            >
              <Box
                sx={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: chartData.dataPoints[index]?.color || '#000',
                  marginRight: '8px',
                  borderRadius: '2px',
                }}
              />
              <Typography variant="body2" sx={{ flex: '1' }}>
                {`${dealer}`}
              </Typography>
              <Typography variant="body2" sx={{ flex: '1' }}>
                {departmentName}
              </Typography>
              <Typography variant="body2" sx={{ flex: '1' }}>
                {`${formatDaysHours(chartData.dataPoints[index]?.y) || 'N/A'}`}
              </Typography>
              <Typography variant="body2" sx={{ flex: '1' }}>
                {`${chartData.vehicleCounts[index] || 0} vehicles`}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography>No dealership data available</Typography>
        )}
      </Box>
    </Box>
  );
};

export default GroupAveragesChart;